"use client";

import { useEffect, useRef, useState } from "react";
import { recommendForProduct, type RecommendForProductInput, type RecommendForProductResult } from "./recommendForProduct";
import { isActiveProfileExplicitlyCleared, PS_STORAGE_CHANGE_EVENT } from "./utils/storage";
import { getStoredProfileSession } from "./utils/profile-auth";

// Process-wide counters so the host page can see how many times the hook
// fires its effect (each fire = a /sizing/sizeguide + /sizing/recommend
// roundtrip unless dedup'd). Diagnostic only — keeps state in a closure
// so React Strict Mode double-renders are visible.
const __ps_diag = { mounts: 0, effectFires: 0 };
if (typeof window !== "undefined") {
  (window as any).__psSdkDiag = __ps_diag;
}

export interface UsePrimeStyleSizeState {
  /** The suggested size (e.g. "M", "42") — null while loading or if no profile */
  recommendedSize: string | null;
  /** Per-section sizes for multi-section products */
  sections: Record<string, string> | null;
  /** True while the recommendation is being fetched */
  loading: boolean;
  /** True if the user has no profile yet — host should show its existing "Find Your Size" CTA */
  noProfile: boolean;
  /** True when the recommendation is tied to a signed-in PrimeStyle profile */
  authenticatedProfile: boolean;
  /** The full result if you need confidence/sections/etc. */
  result: RecommendForProductResult | null;
  /** Manually re-run the recommendation (e.g. after the user edits their profile) */
  refetch: () => void;
}

/**
 * React hook wrapper around recommendForProduct(). Calls the headless API
 * on mount, returns the suggested size + loading state. Re-runs whenever
 * productId changes.
 */
export function usePrimeStyleSize(input: RecommendForProductInput): UsePrimeStyleSizeState {
  const [result, setResult] = useState<RecommendForProductResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);
  const [authenticatedProfile, setAuthenticatedProfile] = useState(false);
  const [tick, setTick] = useState(0);

  // Track per-instance mount count + total effect fires so duplicate
  // consumers / Strict Mode double-mounts are obvious in the console.
  const instanceIdRef = useRef<number>(++__ps_diag.mounts);
  const fireCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setAuthenticatedProfile(Boolean(getStoredProfileSession()));
    setResult(null);
    if (isActiveProfileExplicitlyCleared()) {
      setLoading(false);
      setNoProfile(true);
      return () => { cancelled = true; };
    }
    setLoading(true);
    setNoProfile(false);
    fireCountRef.current += 1;
    __ps_diag.effectFires += 1;
    console.log(
      `[ps-sdk:hook#${instanceIdRef.current}] FIRE #${fireCountRef.current} (global fires=${__ps_diag.effectFires}, mounts=${__ps_diag.mounts})`,
      { productId: input.productId, apiUrl: input.apiUrl, apiKeySet: !!input.apiKey, tick },
    );
    recommendForProduct(input)
      .then((res) => {
        if (cancelled) return;
        if (res === null) {
          console.log("[ps-sdk:hook] usePrimeStyleSize → null (no profile or recommendation failed)");
          setNoProfile(true);
          setResult(null);
        } else {
          console.log("[ps-sdk:hook] usePrimeStyleSize → result", { recommendedSize: res.recommendedSize, hasRaw: !!res.raw });
          setResult(res);
        }
      })
      .catch((e) => {
        console.log("[ps-sdk:hook] usePrimeStyleSize threw", e);
        if (!cancelled) setResult(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    input.productId,
    input.apiUrl,
    input.apiKey,
    input.productCategory,
    input.productSubcategory,
    input.productFitType,
    input.productType,
    input.productTags,
    input.productDescription,
    tick,
  ]);

  // ── Real-time updates ──
  // Refetch whenever the SDK mutates profiles, the active profile, or the
  // size cache. This means the host's product page reacts the instant the
  // user switches profiles inside the SDK or completes a fresh sizing flow,
  // without needing a page reload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = () => {
      setAuthenticatedProfile(Boolean(getStoredProfileSession()));
      setTick((n) => n + 1);
    };
    window.addEventListener(PS_STORAGE_CHANGE_EVENT, onChange);
    // Also catch updates from OTHER tabs/windows via the native storage event.
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(PS_STORAGE_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  return {
    recommendedSize: result?.recommendedSize ?? null,
    sections: result?.sections ?? null,
    loading,
    noProfile,
    authenticatedProfile,
    result,
    refetch: () => setTick((n) => n + 1),
  };
}
